import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  Calculator,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Edit3,
  Info,
  Loader2,
  Percent,
  Plus,
  RefreshCw,
  Save,
  Target,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal.tsx';
import {
  FunnelMetricsResponse,
  getFunnelMetrics,
} from '../services/api.ts';
import {
  ClinicGoal,
  ClinicGoalPayload,
  GoalCalculationBasis,
  GoalPeriodType,
  createClinicGoal,
  deleteClinicGoal,
  getClinicGoals,
  permanentlyDeleteClinicGoal,
  updateClinicGoal,
} from '../services/goalsApi.ts';
import {
  AdCampaignHistoricalCpl,
  getAdCampaignHistoricalCpl,
} from '../services/adCampaignApi.ts';

interface CardProps {
  title: string;
  info?: string;
  right?: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

interface InfoTooltipProps {
  text: string;
  position?: 'bottom' | 'left' | 'right';
}

interface GoalFormState {
  id?: number;
  name: string;
  periodType: GoalPeriodType;
  startDate: string;
  endDate: string;
  calculationBasis: GoalCalculationBasis;
  targetRevenue?: number;
  targetLeads?: number;
  targetAgendamentos?: number;
  targetComparecimentos?: number;
  targetVendas?: number;
  targetPercentAgendamentos?: number;
  targetPercentComparecimentos?: number;
  targetPercentVendas?: number;
}

interface FunnelPlan {
  revenue: number;
  leads: number;
  agendamentos: number;
  comparecimentos: number;
  vendas: number;
  percentAgendamentos: number;
  percentComparecimentos: number;
  percentVendas: number;
  ticketMedio: number;
}

type MetricKey = 'revenue' | 'leads' | 'agendamentos' | 'comparecimentos' | 'vendas';
type PercentField = 'targetPercentAgendamentos' | 'targetPercentComparecimentos' | 'targetPercentVendas';
type ArchivedPeriodFilter = GoalPeriodType | 'all';
type ArchiveStatus = 'achieved' | 'partial' | 'missed';
type BasisOption = {
  value: GoalCalculationBasis;
  label: string;
  icon: React.ElementType;
};
type PeriodOption = {
  value: GoalPeriodType;
  label: string;
  icon: React.ElementType;
};

type CurrencyBuffer = {
  integer: string;
  decimals: string;
  decimalMode: boolean;
};

interface GoalProgressSummary {
  primaryKey: MetricKey;
  target: number;
  actual: number;
  progress: number;
  status: ArchiveStatus;
}

interface ArchivedWeekGroup {
  key: string;
  label: string;
  rangeLabel: string;
  index: number;
  goals: ClinicGoal[];
}

interface CurrencyInputProps {
  value?: number;
  onValueChange: (value: number) => void;
  onFocus?: () => void;
  className?: string;
}

const basisOptions: BasisOption[] = [
  { value: 'revenue', label: 'Valor faturado', icon: DollarSign },
  { value: 'leads', label: 'Leads', icon: Users },
  { value: 'agendamentos', label: 'Agendamentos', icon: CalendarRange },
  { value: 'comparecimentos', label: 'Comparecimentos', icon: CheckCircle2 },
  { value: 'vendas', label: 'Vendas', icon: TrendingUp },
];

const periodOptions: PeriodOption[] = [
  { value: 'monthly', label: 'Mensal', icon: CalendarRange },
  { value: 'weekly', label: 'Semanal', icon: CalendarDays },
  { value: 'daily', label: 'Diária', icon: CalendarDays },
];

const archivedPeriodFilters: Array<{ value: ArchivedPeriodFilter; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'monthly', label: 'Mensais' },
  { value: 'weekly', label: 'Semanais' },
  { value: 'daily', label: 'Diárias' },
];

function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getStartOfCurrentMonth(): string {
  const now = new Date();
  return formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

function getEndOfCurrentMonth(): string {
  const now = new Date();
  return formatDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0));
}

function getTodayDate(): string {
  return formatDateInput(new Date());
}

function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function getStartOfMonthDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getEndOfMonthDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getStartOfCurrentWeek(): string {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return formatDateInput(addDays(now, mondayOffset));
}

function getEndOfCurrentWeek(): string {
  return formatDateInput(addDays(parseLocalDate(getStartOfCurrentWeek()), 6));
}

function getPeriodDefaults(periodType: GoalPeriodType): Pick<GoalFormState, 'startDate' | 'endDate'> {
  if (periodType === 'daily') {
    const today = getTodayDate();
    return { startDate: today, endDate: today };
  }

  if (periodType === 'weekly') {
    return {
      startDate: getStartOfCurrentWeek(),
      endDate: getEndOfCurrentWeek(),
    };
  }

  return {
    startDate: getStartOfCurrentMonth(),
    endDate: getEndOfCurrentMonth(),
  };
}

function getPeriodGoalName(periodType: GoalPeriodType): string {
  if (periodType === 'daily') return 'Meta do dia';
  if (periodType === 'weekly') return 'Meta da semana';
  return 'Meta do mês';
}

function getPeriodLabel(periodType: GoalPeriodType): string {
  if (periodType === 'daily') return 'Diária';
  if (periodType === 'weekly') return 'Semanal';
  return 'Mensal';
}

function formatMonthYear(date: Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function formatDisplayDate(value: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

function getMonthWeekRange(monthDate: Date, weekIndex: number): { start: Date; end: Date } {
  const monthStart = getStartOfMonthDate(monthDate);
  const monthEnd = getEndOfMonthDate(monthDate);
  const start = new Date(monthStart.getFullYear(), monthStart.getMonth(), ((weekIndex - 1) * 7) + 1);
  const end = new Date(monthStart.getFullYear(), monthStart.getMonth(), Math.min((weekIndex * 7), monthEnd.getDate()));

  return { start, end };
}

function getArchivedGoalWeekBucket(goal: ClinicGoal, monthDate: Date): Omit<ArchivedWeekGroup, 'goals'> {
  const monthStart = getStartOfMonthDate(monthDate);
  const monthEnd = getEndOfMonthDate(monthDate);
  const goalStart = parseLocalDate(goal.start_date);
  const clampedStart = goalStart < monthStart ? monthStart : goalStart > monthEnd ? monthEnd : goalStart;
  const index = Math.max(1, Math.ceil(clampedStart.getDate() / 7));
  const range = getMonthWeekRange(monthDate, index);

  return {
    key: `week-${index}`,
    label: `Semana ${index}`,
    rangeLabel: `${formatDisplayDate(formatDateInput(range.start))} - ${formatDisplayDate(formatDateInput(range.end))}`,
    index,
  };
}

function sortGoalsByStartDateAsc(goals: ClinicGoal[]): ClinicGoal[] {
  return [...goals].sort((a, b) => {
    const startDiff = a.start_date.localeCompare(b.start_date);
    if (startDiff !== 0) return startDiff;

    const endDiff = a.end_date.localeCompare(b.end_date);
    if (endDiff !== 0) return endDiff;

    return a.id - b.id;
  });
}

function inclusiveDays(startDate: string, endDate: string): number {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  const diff = end.getTime() - start.getTime();
  return Math.max(1, Math.floor(diff / 86400000) + 1);
}

function remainingDays(endDate: string): number {
  const today = parseLocalDate(formatDateInput(new Date()));
  const end = parseLocalDate(endDate);

  if (end.getTime() < today.getTime()) {
    return 0;
  }

  return inclusiveDays(formatDateInput(today), endDate);
}

function numberOrZero(value?: number | null): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function normalizePercent(value?: number | null): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(100, parsed);
}

function roundMoney(value: number): number {
  return Math.round(Math.max(0, value) * 100) / 100;
}

function roundCount(value: number): number {
  return Math.ceil(Math.max(0, value));
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

function buildCurrencyBuffer(value?: number): CurrencyBuffer {
  const safeValue = Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;
  const [integer, decimals] = safeValue.toFixed(2).split('.');

  return {
    integer,
    decimals: decimals === '00' ? '' : decimals,
    decimalMode: decimals !== '00',
  };
}

function currencyBufferToNumber(buffer: CurrencyBuffer): number {
  const integer = Number(buffer.integer || '0');
  const decimalText = `${buffer.decimals}00`.slice(0, 2);
  const decimals = Number(decimalText || '0') / 100;
  const value = integer + decimals;

  return Number.isFinite(value) ? value : 0;
}

function parseCurrencyPaste(rawValue: string): number {
  const cleanValue = rawValue
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const parsed = Number(cleanValue);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(Math.round(value || 0));
}

function formatPercent(value: number): string {
  return `${(value || 0).toFixed(1)}%`;
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  return fallback;
}

function createDefaultForm(periodType: GoalPeriodType = 'monthly'): GoalFormState {
  const periodDefaults = getPeriodDefaults(periodType);

  return {
    name: getPeriodGoalName(periodType),
    periodType,
    startDate: periodDefaults.startDate,
    endDate: periodDefaults.endDate,
    calculationBasis: 'revenue',
    targetRevenue: 0,
    targetLeads: 0,
    targetAgendamentos: 0,
    targetComparecimentos: 0,
    targetVendas: 0,
    targetPercentAgendamentos: undefined,
    targetPercentComparecimentos: undefined,
    targetPercentVendas: undefined,
  };
}

function goalToForm(goal: ClinicGoal): GoalFormState {
  return {
    id: goal.id,
    name: goal.name,
    periodType: goal.period_type,
    startDate: goal.start_date,
    endDate: goal.end_date,
    calculationBasis: goal.calculation_basis,
    targetRevenue: goal.target_revenue ?? 0,
    targetLeads: goal.target_leads ?? 0,
    targetAgendamentos: goal.target_agendamentos ?? 0,
    targetComparecimentos: goal.target_comparecimentos ?? 0,
    targetVendas: goal.target_vendas ?? 0,
    targetPercentAgendamentos: goal.target_percent_agendamentos ?? undefined,
    targetPercentComparecimentos: goal.target_percent_comparecimentos ?? undefined,
    targetPercentVendas: goal.target_percent_vendas ?? undefined,
  };
}

function getGoalTarget(goal: ClinicGoal, key: MetricKey): number {
  if (key === 'revenue') return numberOrZero(goal.target_revenue);
  if (key === 'leads') return numberOrZero(goal.target_leads);
  if (key === 'agendamentos') return numberOrZero(goal.target_agendamentos);
  if (key === 'comparecimentos') return numberOrZero(goal.target_comparecimentos);
  return numberOrZero(goal.target_vendas);
}

function getActualValue(stats: FunnelMetricsResponse | undefined, key: MetricKey): number {
  if (!stats) return 0;
  if (key === 'revenue') return stats.valorFaturado || 0;
  if (key === 'leads') return stats.totalLeads || 0;
  if (key === 'agendamentos') return stats.totalAgendamentos || 0;
  if (key === 'comparecimentos') return stats.totalComparecimentos || 0;
  return stats.totalVendas || 0;
}

function calculateProgress(actual: number, target: number): number {
  if (!target) return 0;
  return Math.min(100, Math.round((actual / target) * 100));
}

function getPrimaryMetricKey(goal: ClinicGoal): MetricKey {
  return goal.calculation_basis as MetricKey;
}

function getArchiveStatus(progress: number): ArchiveStatus {
  if (progress >= 100) return 'achieved';
  if (progress > 0) return 'partial';
  return 'missed';
}

function getArchiveStatusLabel(status: ArchiveStatus): string {
  if (status === 'achieved') return 'Atingida';
  if (status === 'partial') return 'Parcial';
  return 'Não atingida';
}

function getGoalProgressSummary(goal: ClinicGoal, metrics?: FunnelMetricsResponse): GoalProgressSummary {
  const primaryKey = getPrimaryMetricKey(goal);
  const target = getGoalTarget(goal, primaryKey);
  const actual = getActualValue(metrics, primaryKey);
  const progress = calculateProgress(actual, target);

  return {
    primaryKey,
    target,
    actual,
    progress,
    status: getArchiveStatus(progress),
  };
}

function calculateFunnelPlan(form: GoalFormState, stats: FunnelMetricsResponse | null): FunnelPlan {
  const ticketMedio = numberOrZero(stats?.ticketMedio);

  const percentAgendamentos = normalizePercent(form.targetPercentAgendamentos) || normalizePercent(stats?.percentAgendamentos);
  const percentComparecimentos = normalizePercent(form.targetPercentComparecimentos) || normalizePercent(stats?.percentComparecimentos);
  const percentVendas = normalizePercent(form.targetPercentVendas) || normalizePercent(stats?.percentVendas);

  const agendamentoRate = percentAgendamentos / 100;
  const comparecimentoRate = percentComparecimentos / 100;
  const vendaRate = percentVendas / 100;

  let revenue = 0;
  let leads = 0;
  let agendamentos = 0;
  let comparecimentos = 0;
  let vendas = 0;

  const divideByRate = (value: number, rate: number) => (rate > 0 ? value / rate : 0);

  switch (form.calculationBasis) {
    case 'leads':
      leads = numberOrZero(form.targetLeads);
      agendamentos = leads * agendamentoRate;
      comparecimentos = agendamentos * comparecimentoRate;
      vendas = comparecimentos * vendaRate;
      revenue = vendas * ticketMedio;
      break;
    case 'agendamentos':
      agendamentos = numberOrZero(form.targetAgendamentos);
      leads = divideByRate(agendamentos, agendamentoRate);
      comparecimentos = agendamentos * comparecimentoRate;
      vendas = comparecimentos * vendaRate;
      revenue = vendas * ticketMedio;
      break;
    case 'comparecimentos':
      comparecimentos = numberOrZero(form.targetComparecimentos);
      agendamentos = divideByRate(comparecimentos, comparecimentoRate);
      leads = divideByRate(agendamentos, agendamentoRate);
      vendas = comparecimentos * vendaRate;
      revenue = vendas * ticketMedio;
      break;
    case 'vendas':
      vendas = numberOrZero(form.targetVendas);
      comparecimentos = divideByRate(vendas, vendaRate);
      agendamentos = divideByRate(comparecimentos, comparecimentoRate);
      leads = divideByRate(agendamentos, agendamentoRate);
      revenue = vendas * ticketMedio;
      break;
    case 'revenue':
    default:
      revenue = numberOrZero(form.targetRevenue);
      vendas = ticketMedio > 0 ? revenue / ticketMedio : 0;
      comparecimentos = divideByRate(vendas, vendaRate);
      agendamentos = divideByRate(comparecimentos, comparecimentoRate);
      leads = divideByRate(agendamentos, agendamentoRate);
      break;
  }

  return {
    revenue: roundMoney(revenue),
    leads: roundCount(leads),
    agendamentos: roundCount(agendamentos),
    comparecimentos: roundCount(comparecimentos),
    vendas: roundCount(vendas),
    percentAgendamentos,
    percentComparecimentos,
    percentVendas,
    ticketMedio,
  };
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, position = 'bottom' }) => {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);

  const positionClasses: Record<NonNullable<InfoTooltipProps['position']>, string> = {
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="Mais informações"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
          isDark
            ? 'text-gray-500 hover:bg-gray-700/60 hover:text-sminy-primary'
            : 'text-gray-400 hover:bg-gray-100 hover:text-sminy-primary'
        }`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className={`pointer-events-none absolute z-30 w-64 rounded-xl border p-3 text-xs leading-relaxed shadow-xl ${positionClasses[position]} ${
            isDark
              ? 'border-gray-700 bg-gray-900 text-gray-200'
              : 'border-gray-200 bg-white text-gray-700'
          }`}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const Card: React.FC<CardProps> = ({
  title,
  info,
  right,
  className = '',
  collapsible = false,
  expanded = true,
  onToggle,
  children,
}) => {
  const { isDark } = useTheme();
  const isExpanded = !collapsible || expanded;
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <div className={`rounded-2xl border p-5 shadow-xl transition-colors ${
      isDark ? 'border-gray-700 bg-[#111827] text-white' : 'border-gray-200 bg-white text-gray-900'
    } ${className}`}>
      <div className={`${isExpanded ? 'mb-4' : ''} flex items-center justify-between gap-3`}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {collapsible ? (
            <button
              type="button"
              aria-expanded={isExpanded}
              onClick={onToggle}
              className={`min-w-0 rounded-lg text-left transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/50 ${
                isDark ? 'hover:text-white' : 'hover:text-gray-900'
              }`}
            >
              <h2 className={`truncate text-sm font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h2>
            </button>
          ) : (
            <h2 className={`text-sm font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h2>
          )}
          {info && <InfoTooltip text={info} />}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {right}
          {collapsible && (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Minimizar' : 'Expandir'} ${title}`}
              onClick={onToggle}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/50 ${
                isDark
                  ? 'text-gray-400 hover:bg-gray-700/60 hover:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <ChevronIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
      {isExpanded && children}
    </div>
  );
};

const CurrencyInput: React.FC<CurrencyInputProps> = ({ value = 0, onValueChange, onFocus, className = '' }) => {
  const [buffer, setBuffer] = useState<CurrencyBuffer>(() => buildCurrencyBuffer(value));
  const [focused, setFocused] = useState(false);
  const displayValue = formatCurrency(focused ? currencyBufferToNumber(buffer) : value);

  useEffect(() => {
    if (!focused) {
      setBuffer(buildCurrencyBuffer(value));
    }
  }, [focused, value]);

  const commitBuffer = (nextBuffer: CurrencyBuffer) => {
    setBuffer(nextBuffer);
    onValueChange(roundMoney(currencyBufferToNumber(nextBuffer)));
  };

  const isFullySelected = (input: HTMLInputElement) => (
    input.selectionStart === 0 && input.selectionEnd === input.value.length
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      const baseBuffer = isFullySelected(input)
        ? { integer: '0', decimals: '', decimalMode: false }
        : buffer;
      const nextBuffer = baseBuffer.decimalMode
        ? { ...baseBuffer, decimals: `${baseBuffer.decimals}${event.key}`.slice(0, 2) }
        : { ...baseBuffer, integer: `${baseBuffer.integer}${event.key}`.replace(/^0+(?=\d)/, '') };

      commitBuffer(nextBuffer);
      return;
    }

    if (event.key === ',' || event.key === '.') {
      event.preventDefault();
      commitBuffer({
        ...(isFullySelected(input) ? { integer: '0', decimals: '', decimalMode: false } : buffer),
        decimalMode: true,
      });
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (isFullySelected(input)) {
        commitBuffer({ integer: '0', decimals: '', decimalMode: false });
        return;
      }

      if (buffer.decimalMode && buffer.decimals.length > 0) {
        commitBuffer({ ...buffer, decimals: buffer.decimals.slice(0, -1) });
      } else if (buffer.decimalMode) {
        commitBuffer({ ...buffer, decimalMode: false });
      } else {
        const integer = buffer.integer.slice(0, -1) || '0';
        commitBuffer({ ...buffer, integer });
      }
      return;
    }

    if (event.key === 'Delete' || event.key === 'Escape') {
      event.preventDefault();
      commitBuffer({ integer: '0', decimals: '', decimalMode: false });
      return;
    }

    event.preventDefault();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedValue = parseCurrencyPaste(event.clipboardData.getData('text'));
    setBuffer(buildCurrencyBuffer(pastedValue));
    onValueChange(roundMoney(pastedValue));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={(event) => {
        const input = event.currentTarget;
        setFocused(true);
        setBuffer(buildCurrencyBuffer(value));
        onFocus?.();
        window.requestAnimationFrame(() => input.select());
      }}
      onBlur={() => {
        setFocused(false);
        setBuffer(buildCurrencyBuffer(value));
      }}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      className={className}
    />
  );
};

const Metas: React.FC = () => {
  const { isDark } = useTheme();
  const clinicId = Number(localStorage.getItem('clinic_id') || 0);
  const [form, setForm] = useState<GoalFormState>(() => createDefaultForm());
  const [historicalStats, setHistoricalStats] = useState<FunnelMetricsResponse | null>(null);
  const [adCpl, setAdCpl] = useState<AdCampaignHistoricalCpl | null>(null);
  const [goals, setGoals] = useState<ClinicGoal[]>([]);
  const [archivedGoals, setArchivedGoals] = useState<ClinicGoal[]>([]);
  const [goalToArchive, setGoalToArchive] = useState<ClinicGoal | null>(null);
  const [archivedGoalToDelete, setArchivedGoalToDelete] = useState<ClinicGoal | null>(null);
  const [goalMetrics, setGoalMetrics] = useState<Record<number, FunnelMetricsResponse>>({});
  const [archivedGoalMetrics, setArchivedGoalMetrics] = useState<Record<number, FunnelMetricsResponse>>({});
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingAdCpl, setLoadingAdCpl] = useState(false);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [loadingArchivedGoals, setLoadingArchivedGoals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [planExpanded, setPlanExpanded] = useState(false);
  const [activeGoalsExpanded, setActiveGoalsExpanded] = useState(true);
  const [clinicHistoryExpanded, setClinicHistoryExpanded] = useState(true);
  const [archivedGoalsExpanded, setArchivedGoalsExpanded] = useState(true);
  const [archiveMonthDate, setArchiveMonthDate] = useState<Date>(() => getStartOfMonthDate(addMonths(new Date(), -1)));
  const [archivePeriodFilter, setArchivePeriodFilter] = useState<ArchivedPeriodFilter>('all');
  const [expandedArchivedGoalIds, setExpandedArchivedGoalIds] = useState<Set<number>>(() => new Set<number>());

  const plan = useMemo(() => calculateFunnelPlan(form, historicalStats), [form, historicalStats]);
  const archivedMonthStart = useMemo(() => formatDateInput(getStartOfMonthDate(archiveMonthDate)), [archiveMonthDate]);
  const archivedMonthEnd = useMemo(() => formatDateInput(getEndOfMonthDate(archiveMonthDate)), [archiveMonthDate]);
  const archivedMonthLabel = useMemo(() => formatMonthYear(archiveMonthDate), [archiveMonthDate]);
  const suggestedAdsInvestment = useMemo(() => {
    const cpl = numberOrZero(adCpl?.cpl);
    return cpl > 0 ? roundMoney(plan.leads * cpl) : 0;
  }, [adCpl?.cpl, plan.leads]);
  const hasHistoricalCpl = numberOrZero(adCpl?.cpl) > 0 && numberOrZero(adCpl?.totalLeads) > 0;
  const activePeriodType = useMemo<GoalPeriodType | null>(() => {
    const activeGoal = goals.find((goal) => goal.active);
    return activeGoal?.period_type ?? null;
  }, [goals]);
  const activePeriodLabel = activePeriodType ? getPeriodLabel(activePeriodType).toLowerCase() : '';

  const loadGoals = useCallback(async () => {
    setLoadingGoals(true);
    try {
      const loadedGoals = await getClinicGoals({ activeOnly: true });
      const orderedGoals = sortGoalsByStartDateAsc(loadedGoals);
      setGoals(orderedGoals);

      if (orderedGoals.length === 0) {
        setGoalMetrics({});
        return;
      }

      const metricsEntries = await Promise.all(
        orderedGoals.map(async (goal) => {
          const metrics = await getFunnelMetrics({
            clinicId: goal.clinic_id,
            startDate: goal.start_date,
            endDate: goal.end_date,
          });
          return [goal.id, metrics] as const;
        }),
      );

      setGoalMetrics(metricsEntries.reduce<Record<number, FunnelMetricsResponse>>((acc, [goalId, metrics]) => {
        acc[goalId] = metrics;
        return acc;
      }, {}));
    } catch (err) {
      console.error('[Metas] Erro ao carregar metas:', err);
      setError('Não foi possível carregar as metas.');
    } finally {
      setLoadingGoals(false);
    }
  }, []);

  const loadArchivedGoals = useCallback(async () => {
    setLoadingArchivedGoals(true);
    try {
      const loadedGoals = await getClinicGoals({
        activeOnly: false,
        startDate: archivedMonthStart,
        endDate: archivedMonthEnd,
      });
      const archived = sortGoalsByStartDateAsc(loadedGoals.filter((goal) => !goal.active));
      setArchivedGoals(archived);
      setExpandedArchivedGoalIds(new Set<number>());

      if (archived.length === 0) {
        setArchivedGoalMetrics({});
        return;
      }

      const metricsEntries = await Promise.all(
        archived.map(async (goal) => {
          const metrics = await getFunnelMetrics({
            clinicId: goal.clinic_id,
            startDate: goal.start_date,
            endDate: goal.end_date,
          });
          return [goal.id, metrics] as const;
        }),
      );

      setArchivedGoalMetrics(metricsEntries.reduce<Record<number, FunnelMetricsResponse>>((acc, [goalId, metrics]) => {
        acc[goalId] = metrics;
        return acc;
      }, {}));
    } catch (err) {
      console.error('[Metas] Erro ao carregar metas arquivadas:', err);
      setError('Não foi possível carregar o histórico de metas.');
    } finally {
      setLoadingArchivedGoals(false);
    }
  }, [archivedMonthEnd, archivedMonthStart]);

  const loadHistoricalMetrics = useCallback(async () => {
    if (!clinicId) {
      setError('Clínica não identificada. Faça login novamente.');
      return;
    }

    setLoadingMetrics(true);
    setError('');

    try {
      const data = await getFunnelMetrics({
        clinicId,
      });
      setHistoricalStats(data);
    } catch (err) {
      console.error('[Metas] Erro ao carregar base histórica:', err);
      setError('Não foi possível carregar a base histórica da calculadora.');
    } finally {
      setLoadingMetrics(false);
    }
  }, [clinicId]);

  const loadAdCpl = useCallback(async () => {
    if (!clinicId) {
      return;
    }

    setLoadingAdCpl(true);

    try {
      const data = await getAdCampaignHistoricalCpl(clinicId);
      setAdCpl(data);
    } catch (err) {
      console.error('[Metas] Erro ao carregar CPL histórico de anúncios:', err);
      setAdCpl(null);
    } finally {
      setLoadingAdCpl(false);
    }
  }, [clinicId]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  useEffect(() => {
    loadArchivedGoals();
  }, [loadArchivedGoals]);

  useEffect(() => {
    loadHistoricalMetrics();
  }, [loadHistoricalMetrics]);

  useEffect(() => {
    loadAdCpl();
  }, [loadAdCpl]);

  useEffect(() => {
    if (form.id) {
      setPlanExpanded(true);
    }
  }, [form.id]);

  useEffect(() => {
    if (!activePeriodType || form.id || form.periodType === activePeriodType) {
      return;
    }

    setForm((current) => {
      if (current.id || current.periodType === activePeriodType) {
        return current;
      }

      const periodDefaults = getPeriodDefaults(activePeriodType);
      const currentDefaultName = getPeriodGoalName(current.periodType);

      return {
        ...current,
        periodType: activePeriodType,
        startDate: periodDefaults.startDate,
        endDate: periodDefaults.endDate,
        name: current.name === currentDefaultName ? getPeriodGoalName(activePeriodType) : current.name,
      };
    });
  }, [activePeriodType, form.id, form.periodType]);

  const updateField = <K extends keyof GoalFormState>(field: K, value: GoalFormState[K]) => {
    setSuccess('');
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateNumericField = (field: keyof GoalFormState, rawValue: string, basis?: GoalCalculationBasis) => {
    const numericValue = rawValue === '' ? 0 : Number(rawValue);
    setSuccess('');
    setForm((current) => ({
      ...current,
      ...(basis ? { calculationBasis: basis } : {}),
      [field]: Number.isFinite(numericValue) ? numericValue : 0,
    }));
  };

  const updateCurrencyField = (field: keyof GoalFormState, value: number, basis?: GoalCalculationBasis) => {
    setSuccess('');
    setForm((current) => ({
      ...current,
      ...(basis ? { calculationBasis: basis } : {}),
      [field]: Number.isFinite(value) ? value : 0,
    }));
  };

  const updatePercentField = (field: PercentField, rawValue: string) => {
    const numericValue = rawValue === '' ? undefined : normalizePercent(Number(rawValue));
    setSuccess('');
    setForm((current) => ({
      ...current,
      [field]: numericValue,
    }));
  };

  const updatePeriodType = (periodType: GoalPeriodType) => {
    if (activePeriodType && activePeriodType !== periodType) {
      return;
    }

    const periodDefaults = getPeriodDefaults(periodType);
    setSuccess('');
    setForm((current) => {
      const currentDefaultName = getPeriodGoalName(current.periodType);

      return {
        ...current,
        periodType,
        startDate: periodDefaults.startDate,
        endDate: periodDefaults.endDate,
        name: current.name === currentDefaultName ? getPeriodGoalName(periodType) : current.name,
      };
    });
  };

  const updateStartDate = (value: string) => {
    setSuccess('');
    setForm((current) => {
      if (!value) {
        return {
          ...current,
          startDate: '',
          endDate: current.periodType === 'monthly' ? current.endDate : '',
        };
      }

      if (current.periodType === 'daily') {
        return { ...current, startDate: value, endDate: value };
      }

      if (current.periodType === 'weekly') {
        const suggestedEndDate = formatDateInput(addDays(parseLocalDate(value), 6));

        return {
          ...current,
          startDate: value,
          endDate: suggestedEndDate,
        };
      }

      return { ...current, startDate: value };
    });
  };

  const updateEndDate = (value: string) => {
    setSuccess('');
    setForm((current) => ({
      ...current,
      endDate: current.periodType === 'daily' ? current.startDate : value || '',
    }));
  };

  const resetForm = () => {
    setForm(createDefaultForm(activePeriodType ?? 'monthly'));
    setSuccess('');
    setError('');
  };

  const buildPayload = (): ClinicGoalPayload => ({
    name: form.name.trim() || 'Meta sem nome',
    period_type: form.periodType,
    start_date: form.startDate,
    end_date: form.endDate,
    mode: 'absolute',
    calculation_basis: form.calculationBasis,
    target_revenue: plan.revenue || null,
    target_leads: plan.leads || null,
    target_agendamentos: plan.agendamentos || null,
    target_comparecimentos: plan.comparecimentos || null,
    target_vendas: plan.vendas || null,
    target_percent_agendamentos: plan.percentAgendamentos || null,
    target_percent_comparecimentos: plan.percentComparecimentos || null,
    target_percent_vendas: plan.percentVendas || null,
    target_ticket_medio: plan.ticketMedio || null,
    source_filter: null,
    tag_ids: [],
    active: true,
  });

  const handleSave = async () => {
    if (!form.startDate || !form.endDate || form.endDate < form.startDate) {
      setError('Período inválido para a meta.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const editingGoalId = form.id;
      const payload = buildPayload();
      const savedGoal = editingGoalId
        ? await updateClinicGoal(editingGoalId, payload)
        : await createClinicGoal(payload);

      setForm(editingGoalId ? goalToForm(savedGoal) : createDefaultForm(savedGoal.period_type));
      setSuccess(editingGoalId ? 'Meta atualizada.' : 'Meta criada.');
      await loadGoals();
    } catch (err) {
      console.error('[Metas] Erro ao salvar meta:', err);
      setError(extractApiErrorMessage(err, 'Não foi possível salvar a meta.'));
    } finally {
      setSaving(false);
    }
  };

  const openArchiveModal = (goal: ClinicGoal) => {
    setGoalToArchive(goal);
    setError('');
    setSuccess('');
  };

  const openDeleteArchivedGoalModal = (goal: ClinicGoal) => {
    setArchivedGoalToDelete(goal);
    setError('');
    setSuccess('');
  };

  const handleArchiveGoal = async () => {
    if (!goalToArchive) return;

    const goal = goalToArchive;
    setError('');
    setSuccess('');

    try {
      await deleteClinicGoal(goal.id);
      setSuccess('Meta arquivada.');
      if (form.id === goal.id) {
        resetForm();
      }
      await loadGoals();
      await loadArchivedGoals();
    } catch (err) {
      console.error('[Metas] Erro ao arquivar meta:', err);
      setError('Não foi possível arquivar a meta.');
    } finally {
      setGoalToArchive(null);
    }
  };

  const handleDeleteArchivedGoal = async () => {
    if (!archivedGoalToDelete) return;

    const goal = archivedGoalToDelete;
    setError('');
    setSuccess('');

    try {
      await permanentlyDeleteClinicGoal(goal.id);
      setSuccess('Meta arquivada excluída definitivamente.');
      setExpandedArchivedGoalIds((current) => {
        const next = new Set(current);
        next.delete(goal.id);
        return next;
      });
      await loadArchivedGoals();
    } catch (err) {
      console.error('[Metas] Erro ao excluir meta arquivada:', err);
      setError(extractApiErrorMessage(err, 'Não foi possível excluir a meta arquivada.'));
    } finally {
      setArchivedGoalToDelete(null);
    }
  };

  const metricDefinitions: Array<{ key: MetricKey; label: string; icon: React.ElementType; format: (value: number) => string }> = [
    { key: 'revenue', label: 'Valor faturado', icon: DollarSign, format: formatCurrency },
    { key: 'leads', label: 'Leads', icon: Users, format: formatNumber },
    { key: 'agendamentos', label: 'Agendamentos', icon: CalendarRange, format: formatNumber },
    { key: 'comparecimentos', label: 'Comparecimentos', icon: CheckCircle2, format: formatNumber },
    { key: 'vendas', label: 'Vendas', icon: TrendingUp, format: formatNumber },
  ];
  const basisFieldMap: Record<GoalCalculationBasis, keyof GoalFormState> = {
    revenue: 'targetRevenue',
    leads: 'targetLeads',
    agendamentos: 'targetAgendamentos',
    comparecimentos: 'targetComparecimentos',
    vendas: 'targetVendas',
  };
  const selectedBasisOption = basisOptions.find((option) => option.value === form.calculationBasis) || basisOptions[0];
  const selectedBasisField = basisFieldMap[form.calculationBasis];
  const SelectedBasisIcon = selectedBasisOption.icon;
  const fieldClassName = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary ${
    isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'
  }`;
  const percentFieldClassName = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary ${
    isDark ? 'border-gray-700 bg-gray-950 text-white' : 'border-gray-200 bg-white text-gray-900'
  }`;
  const conversionInputs = [
    {
      field: 'targetPercentAgendamentos' as PercentField,
      label: '% agendamento',
      historical: normalizePercent(historicalStats?.percentAgendamentos),
      current: plan.percentAgendamentos,
    },
    {
      field: 'targetPercentComparecimentos' as PercentField,
      label: '% comparecimento',
      historical: normalizePercent(historicalStats?.percentComparecimentos),
      current: plan.percentComparecimentos,
    },
    {
      field: 'targetPercentVendas' as PercentField,
      label: '% venda',
      historical: normalizePercent(historicalStats?.percentVendas),
      current: plan.percentVendas,
    },
  ];
  const filteredArchivedGoals = useMemo(() => (
    archivePeriodFilter === 'all'
      ? archivedGoals
      : archivedGoals.filter((goal) => goal.period_type === archivePeriodFilter)
  ), [archivePeriodFilter, archivedGoals]);
  const archivedMonthlyGoals = useMemo(() => (
    filteredArchivedGoals.filter((goal) => goal.period_type === 'monthly')
  ), [filteredArchivedGoals]);
  const archivedWeekGroups = useMemo<ArchivedWeekGroup[]>(() => {
    const groups = new Map<string, ArchivedWeekGroup>();

    filteredArchivedGoals
      .filter((goal) => goal.period_type !== 'monthly')
      .forEach((goal) => {
        const bucket = getArchivedGoalWeekBucket(goal, archiveMonthDate);
        const currentGroup = groups.get(bucket.key);

        if (currentGroup) {
          currentGroup.goals.push(goal);
          return;
        }

        groups.set(bucket.key, {
          ...bucket,
          goals: [goal],
        });
      });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        goals: [...group.goals].sort((a, b) => a.start_date.localeCompare(b.start_date)),
      }))
      .sort((a, b) => a.index - b.index);
  }, [archiveMonthDate, filteredArchivedGoals]);
  const archivedSummary = useMemo(() => {
    const count = filteredArchivedGoals.length;
    let achieved = 0;
    let progressTotal = 0;
    let plannedRevenue = 0;
    let actualRevenue = 0;

    filteredArchivedGoals.forEach((goal) => {
      const metrics = archivedGoalMetrics[goal.id];
      const progress = getGoalProgressSummary(goal, metrics);

      if (progress.status === 'achieved') {
        achieved += 1;
      }

      progressTotal += progress.progress;
      plannedRevenue += getGoalTarget(goal, 'revenue');
      actualRevenue += getActualValue(metrics, 'revenue');
    });

    return {
      count,
      achieved,
      averageProgress: count > 0 ? progressTotal / count : 0,
      plannedRevenue,
      actualRevenue,
    };
  }, [archivedGoalMetrics, filteredArchivedGoals]);
  const toggleArchivedGoalDetails = (goalId: number) => {
    setExpandedArchivedGoalIds((current) => {
      const next = new Set(current);

      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }

      return next;
    });
  };
  const archiveStatusBadgeClasses = (status: ArchiveStatus) => {
    if (status === 'achieved') {
      return isDark ? 'bg-emerald-950/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700';
    }

    if (status === 'partial') {
      return isDark ? 'bg-amber-950/40 text-amber-300' : 'bg-amber-100 text-amber-700';
    }

    return isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600';
  };
  const renderArchivedGoalCard = (goal: ClinicGoal) => {
    const metrics = archivedGoalMetrics[goal.id];
    const primaryProgress = getGoalProgressSummary(goal, metrics);
    const primaryMetric = metricDefinitions.find((metric) => metric.key === primaryProgress.primaryKey) || metricDefinitions[0];
    const isExpanded = expandedArchivedGoalIds.has(goal.id);

    return (
      <div
        key={goal.id}
        className={`rounded-xl border p-4 ${
          isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-semibold">{goal.name}</h3>
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                {getPeriodLabel(goal.period_type)}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${archiveStatusBadgeClasses(primaryProgress.status)}`}>
                {getArchiveStatusLabel(primaryProgress.status)}
              </span>
            </div>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatDisplayDate(goal.start_date)} - {formatDisplayDate(goal.end_date)}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:min-w-[300px] lg:justify-end">
            <div className="text-left lg:text-right">
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{primaryMetric.label}</div>
              <div className="text-sm font-semibold">
                {primaryMetric.format(primaryProgress.actual)} de {primaryMetric.format(primaryProgress.target)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openDeleteArchivedGoalModal(goal)}
                className={`inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                  isDark
                    ? 'border-red-900/50 bg-red-950/20 text-red-300 hover:bg-red-950/40'
                    : 'border-red-200 bg-white text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 className="h-4 w-4" />
                Excluir
              </button>
              <button
                type="button"
                onClick={() => toggleArchivedGoalDetails(goal.id)}
                className={`inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                  isDark
                    ? 'border-gray-700 bg-gray-950 text-gray-200 hover:bg-gray-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Detalhes
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between gap-2 text-xs">
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Progresso principal</span>
            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{formatPercent(primaryProgress.progress)}</span>
          </div>
          <div className={`h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div className="h-2 rounded-full bg-sminy-primary" style={{ width: `${primaryProgress.progress}%` }} />
          </div>
        </div>

        {isExpanded && (
          <div className={`mt-4 grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-2 xl:grid-cols-5 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            {metricDefinitions.map((metric) => {
              const target = getGoalTarget(goal, metric.key);
              const actual = getActualValue(metrics, metric.key);
              const progress = calculateProgress(actual, target);

              return (
                <div key={metric.key}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{metric.label}</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{formatPercent(progress)}</span>
                  </div>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
                    <div className="h-2 rounded-full bg-sminy-primary" style={{ width: `${progress}%` }} />
                  </div>
                  <div className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {metric.format(actual)} de {metric.format(target)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`min-h-screen w-full transition-colors ${
      isDark ? 'bg-[#0f172a] text-white' : 'bg-gradient-to-b from-gray-50 to-white text-gray-900'
    }`}>
      {/* STICKY HEADER */}
      <header className={`sticky top-0 z-20 border-b backdrop-blur ${
        isDark ? 'border-gray-700 bg-[#0f172a]/85' : 'border-gray-200 bg-white/85'
      }`}>
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
              isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
            }`}>
              <Target className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Metas</h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Faturamento, volume e conversão do funil
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                loadHistoricalMetrics();
                loadAdCpl();
                loadGoals();
                loadArchivedGoals();
              }}
              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
                isDark
                  ? 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {(error || success) && (
          <div className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300'
          }`}>
            <div className="flex items-center gap-2">
              {error ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              <span>{error || success}</span>
            </div>
          </div>
        )}

        {/* ============================ */}
        {/* ZONA 1 · ACOMPANHAR          */}
        {/* ============================ */}
        <div className={`mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sminy-primary" />
          Acompanhar
        </div>

        <div className="mb-8 space-y-4">
          <Card
            title="Metas Ativas"
            info="Suas metas em andamento com progresso em tempo real. Cada barra mostra quanto já foi alcançado e a média necessária por dia para fechar o período."
            right={loadingGoals ? <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" /> : <Target className="h-4 w-4 text-sminy-primary" />}
            collapsible
            expanded={activeGoalsExpanded}
            onToggle={() => setActiveGoalsExpanded((current) => !current)}
          >
            {goals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${
                  isDark ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <Target className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
                <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  Nenhuma meta ativa
                </h3>
                <p className={`max-w-sm text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Crie uma nova meta na seção <strong>Planejar</strong> abaixo para começar a acompanhar seus resultados.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {goals.map((goal) => {
                  const metrics = goalMetrics[goal.id];
                  const daysRemaining = remainingDays(goal.end_date);

                  return (
                    <div
                      key={goal.id}
                      className={`rounded-xl border p-4 ${
                        isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold">{goal.name}</h3>
                            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                              {getPeriodLabel(goal.period_type)}
                            </span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              daysRemaining > 0
                                ? isDark ? 'bg-emerald-950/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                                : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {daysRemaining > 0 ? `${daysRemaining} dias restantes` : 'Encerrada'}
                            </span>
                          </div>
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatDisplayDate(goal.start_date)} - {formatDisplayDate(goal.end_date)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setForm(goalToForm(goal))}
                            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
                              isDark
                                ? 'border-gray-700 bg-gray-950 text-gray-200 hover:bg-gray-800'
                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <Edit3 className="h-4 w-4" />
                            Editar
                          </button>
                          <button
                            onClick={() => openArchiveModal(goal)}
                            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
                              isDark
                                ? 'border-red-900/50 bg-red-950/20 text-red-300 hover:bg-red-950/40'
                                : 'border-red-200 bg-white text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Arquivar
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
                        {metricDefinitions.map((metric) => {
                          const target = getGoalTarget(goal, metric.key);
                          const actual = getActualValue(metrics, metric.key);
                          const progress = calculateProgress(actual, target);
                          const remaining = Math.max(0, target - actual);
                          const dailyNeed = daysRemaining > 0 ? remaining / daysRemaining : 0;

                          return (
                            <div key={metric.key}>
                              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{metric.label}</span>
                                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{progress}%</span>
                              </div>
                              <div className={`h-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
                                <div className="h-2 rounded-full bg-sminy-primary" style={{ width: `${progress}%` }} />
                              </div>
                              <div className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {metric.format(actual)} de {metric.format(target)}
                              </div>
                              <div className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {daysRemaining > 0 ? `${metric.format(dailyNeed)}/dia` : 'Período encerrado'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card
            title="Histórico da Clínica"
            info="Médias históricas da sua clínica usadas como referência. Quando você não preenche taxas no Passo 2, esses valores são aplicados automaticamente nos cálculos."
            right={loadingMetrics ? <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" /> : <TrendingUp className="h-4 w-4 text-sminy-primary" />}
            collapsible
            expanded={clinicHistoryExpanded}
            onToggle={() => setClinicHistoryExpanded((current) => !current)}
          >
            <p className={`mb-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Base usada como referência para projeções automáticas
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              {[
                { label: '% agendamento', value: historicalStats?.percentAgendamentos || 0, icon: Percent, formatter: formatPercent },
                { label: '% comparecimento', value: historicalStats?.percentComparecimentos || 0, icon: Percent, formatter: formatPercent },
                { label: '% venda', value: historicalStats?.percentVendas || 0, icon: Percent, formatter: formatPercent },
                { label: 'Ticket médio', value: historicalStats?.ticketMedio || 0, icon: DollarSign, formatter: formatCurrency },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</span>
                      <Icon className="h-4 w-4 text-sminy-primary" />
                    </div>
                    <div className="text-lg font-semibold">{item.formatter(item.value)}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* ============================ */}
        {/* ZONA 2 · HISTÓRICO           */}
        {/* ============================ */}
        <div className={`mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sminy-primary" />
          Histórico
        </div>

        <div className="mb-8">
          <Card
            title="Metas Arquivadas"
            info="Metas arquivadas do mês selecionado, agrupadas por metas mensais e semanas. O progresso usa o mesmo período salvo em cada meta."
            right={loadingArchivedGoals ? <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" /> : <Archive className="h-4 w-4 text-sminy-primary" />}
            collapsible
            expanded={archivedGoalsExpanded}
            onToggle={() => setArchivedGoalsExpanded((current) => !current)}
          >
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className={`inline-flex items-center rounded-xl border p-1 ${
                isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
              }`}>
                <button
                  type="button"
                  aria-label="Mês anterior"
                  onClick={() => setArchiveMonthDate((current) => addMonths(current, -1))}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                    isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-white'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="min-w-[168px] px-3 text-center text-sm font-semibold capitalize">
                  {archivedMonthLabel}
                </div>
                <button
                  type="button"
                  aria-label="Próximo mês"
                  onClick={() => setArchiveMonthDate((current) => addMonths(current, 1))}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                    isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-white'
                  }`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {archivedPeriodFilters.map((filter) => {
                  const selected = archivePeriodFilter === filter.value;

                  return (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setArchivePeriodFilter(filter.value)}
                      className={`rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${
                        selected
                          ? 'border-sminy-primary bg-sminy-primary/10 text-sminy-primary-hover ring-1 ring-sminy-primary/40'
                          : isDark
                            ? 'border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { label: 'Arquivadas', value: formatNumber(archivedSummary.count), icon: Archive },
                { label: 'Metas batidas', value: `${formatNumber(archivedSummary.achieved)} de ${formatNumber(archivedSummary.count)}`, icon: CheckCircle2 },
                { label: 'Faturamento', value: `${formatCurrency(archivedSummary.actualRevenue)} de ${formatCurrency(archivedSummary.plannedRevenue)}`, icon: DollarSign },
                { label: 'Progresso médio', value: formatPercent(archivedSummary.averageProgress), icon: Percent },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.label}
                    className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</span>
                      <Icon className="h-4 w-4 shrink-0 text-sminy-primary" />
                    </div>
                    <div className="break-words text-base font-semibold">{item.value}</div>
                  </div>
                );
              })}
            </div>

            {loadingArchivedGoals ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-2 text-sm text-sminy-primary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando histórico...
                </div>
              </div>
            ) : filteredArchivedGoals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${
                  isDark ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <Archive className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
                <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  Nenhuma meta arquivada
                </h3>
                <p className={`max-w-sm text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Não há metas arquivadas para o período e filtro selecionados.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {archivedMonthlyGoals.length > 0 && (
                  <section className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Metas mensais</h3>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatNumber(archivedMonthlyGoals.length)} arquivada(s)</p>
                      </div>
                      <CalendarRange className="h-4 w-4 text-sminy-primary" />
                    </div>
                    <div className="space-y-3">
                      {archivedMonthlyGoals.map(renderArchivedGoalCard)}
                    </div>
                  </section>
                )}

                {archivedWeekGroups.map((group) => (
                  <section key={group.key} className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{group.label}</h3>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {group.rangeLabel} · {formatNumber(group.goals.length)} arquivada(s)
                        </p>
                      </div>
                      <CalendarDays className="h-4 w-4 text-sminy-primary" />
                    </div>
                    <div className="space-y-3">
                      {group.goals.map(renderArchivedGoalCard)}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ============================ */}
        {/* ZONA 3 · PLANEJAR            */}
        {/* ============================ */}
        <div className={`mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sminy-primary" />
          Planejar
        </div>

        <div className={`overflow-hidden rounded-2xl border shadow-xl transition-colors ${
          isDark ? 'border-gray-700 bg-[#111827] text-white' : 'border-gray-200 bg-white text-gray-900'
        }`}>
          <button
            type="button"
            onClick={() => setPlanExpanded((current) => !current)}
            className={`flex w-full items-center justify-between gap-3 p-5 text-left transition-colors ${
              isDark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                planExpanded
                  ? 'bg-sminy-primary/20 text-sminy-primary'
                  : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}>
                {form.id ? <Edit3 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              </div>
              <div>
                <h2 className={`text-sm font-semibold tracking-tight ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                  {form.id ? 'Editar Meta' : 'Nova Meta'}
                </h2>
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {planExpanded
                    ? 'Defina período, base de cálculo e veja a projeção do funil'
                    : form.id
                      ? 'Clique para retomar a edição'
                      : 'Clique para criar uma nova meta com calculadora e sugestão de Ads'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {planExpanded && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    resetForm();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      resetForm();
                    }
                  }}
                  className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs transition-colors ${
                    isDark
                      ? 'text-gray-400 hover:bg-gray-700/40 hover:text-gray-200'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Limpar
                </span>
              )}
              {loadingMetrics && <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" />}
              {planExpanded
                ? <ChevronDown className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                : <ChevronRight className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />}
            </div>
          </button>

          {planExpanded && (
            <div className={`space-y-5 border-t p-5 ${
              isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50/30'
            }`}>
            {/* PASSO 1 · PERÍODO */}
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                }`}>
                  1
                </span>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Período</h3>
                <InfoTooltip text="Mensal, semanal ou diária. A periodicidade determina o cálculo do esforço diário e só pode haver uma meta ativa por tipo de período." />
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Nome</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => updateField('name', event.target.value)}
                    className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary ${
                      isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'
                    }`}
                  />
                </div>

                <div>
                  <label className={`mb-2 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Periodicidade</label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {periodOptions.map((option) => {
                      const Icon = option.icon;
                      const selected = form.periodType === option.value;
                      const disabled = Boolean(activePeriodType && activePeriodType !== option.value);

                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={disabled}
                          onClick={() => updatePeriodType(option.value)}
                          className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            selected
                              ? 'border-sminy-primary bg-sminy-primary/10 text-sminy-primary-hover ring-1 ring-sminy-primary/40'
                              : isDark
                                ? 'border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800'
                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {activePeriodType && (
                    <div className={`mt-2 rounded-xl border px-3 py-2 text-xs ${
                      isDark
                        ? 'border-blue-900/50 bg-blue-950/20 text-blue-200'
                        : 'border-blue-200 bg-blue-50 text-blue-700'
                    }`}>
                      Já existe meta ativa {activePeriodLabel}. Para usar outra periodicidade, arquive as metas ativas atuais.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className={form.periodType === 'daily' ? 'sm:col-span-2' : ''}>
                    <label className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {form.periodType === 'daily' ? 'Dia' : form.periodType === 'weekly' ? 'Início da semana' : 'Início'}
                    </label>
                    <input
                      type="date"
                      value={form.startDate}
                      onChange={(event) => updateStartDate(event.target.value)}
                      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary ${
                        isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  {form.periodType !== 'daily' && (
                    <div>
                      <label className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {form.periodType === 'weekly' ? 'Fim da semana' : 'Fim'}
                      </label>
                      <input
                        type="date"
                        value={form.endDate}
                        min={form.startDate || undefined}
                        onChange={(event) => updateEndDate(event.target.value)}
                        className={`w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sminy-primary disabled:cursor-not-allowed disabled:opacity-70 ${
                          isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'
                        }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* PASSO 2 · BASE DE CÁLCULO */}
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                }`}>
                  2
                </span>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Base de cálculo</h3>
                <InfoTooltip text="Escolha a partir de qual métrica você quer planejar (ex: faturamento, leads, agendamentos). As outras métricas do funil serão calculadas automaticamente." />
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`mb-2 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Calcular meta a partir de</label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                    {basisOptions.map((option) => {
                      const Icon = option.icon;
                      const selected = form.calculationBasis === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updateField('calculationBasis', option.value)}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                            selected
                              ? 'border-sminy-primary bg-sminy-primary/10 text-sminy-primary-hover ring-1 ring-sminy-primary/40'
                              : isDark
                                ? 'border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800'
                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="font-medium">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  <label className={`mb-2 flex items-center gap-2 text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <SelectedBasisIcon className="h-4 w-4 text-sminy-primary" />
                    Meta de {selectedBasisOption.label.toLowerCase()}
                  </label>
                  {form.calculationBasis === 'revenue' ? (
                    <CurrencyInput
                      value={form.targetRevenue ?? 0}
                      onValueChange={(value) => updateCurrencyField('targetRevenue', value, 'revenue')}
                      className={fieldClassName}
                    />
                  ) : (
                    <input
                      type="number"
                      min="0"
                      value={String(form[selectedBasisField] ?? '')}
                      onChange={(event) => updateNumericField(selectedBasisField, event.target.value, form.calculationBasis)}
                      className={fieldClassName}
                    />
                  )}
                </div>

                <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Percent className="h-4 w-4 text-sminy-primary" />
                      <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Taxas usadas no cálculo
                      </span>
                    </div>
                    {(form.targetPercentAgendamentos || form.targetPercentComparecimentos || form.targetPercentVendas) && (
                      <button
                        type="button"
                        onClick={() => setForm((current) => ({
                          ...current,
                          targetPercentAgendamentos: undefined,
                          targetPercentComparecimentos: undefined,
                          targetPercentVendas: undefined,
                        }))}
                        className={`text-xs font-medium ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        Usar histórico
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {conversionInputs.map((input) => (
                      <div key={input.field}>
                        <label className={`mb-1 block text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {input.label}
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={form[input.field] ?? ''}
                          placeholder={formatPercent(input.historical)}
                          onChange={(event) => updatePercentField(input.field, event.target.value)}
                          className={percentFieldClassName}
                        />
                        <div className={`mt-1 flex items-center justify-between gap-2 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          <span>Histórico {formatPercent(input.historical)}</span>
                          <span>Usado {formatPercent(input.current)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PASSO 3 · PROJEÇÃO DO FUNIL */}
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                  }`}>
                    3
                  </span>
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Projeção do funil</h3>
                  <InfoTooltip text="Valores calculados automaticamente a partir da meta definida no Passo 2, das taxas de conversão e do ticket médio histórico." />
                </div>
                <Calculator className="h-4 w-4 text-sminy-primary" />
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {metricDefinitions.map((metric) => {
                    const Icon = metric.icon;
                    const value = metric.key === 'revenue'
                      ? plan.revenue
                      : plan[metric.key];

                    return (
                      <div
                        key={metric.key}
                        className={`rounded-xl border p-3 ${
                          isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{metric.label}</span>
                          <Icon className="h-4 w-4 text-sminy-primary" />
                        </div>
                        <div className="text-lg font-semibold">{metric.format(value)}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>% agendamento</span>
                    <div className="mt-1 text-lg font-semibold">{formatPercent(plan.percentAgendamentos)}</div>
                  </div>
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>% comparecimento</span>
                    <div className="mt-1 text-lg font-semibold">{formatPercent(plan.percentComparecimentos)}</div>
                  </div>
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>% venda</span>
                    <div className="mt-1 text-lg font-semibold">{formatPercent(plan.percentVendas)}</div>
                  </div>
                  <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ticket médio</span>
                    <div className="mt-1 text-lg font-semibold">{formatCurrency(plan.ticketMedio)}</div>
                  </div>
                </div>

                {/* Sugestão de Ads embutida no Passo 3 */}
                <div className={`rounded-xl border p-4 ${isDark ? 'border-sminy-primary/30 bg-sminy-primary/5' : 'border-sminy-primary/20 bg-sminy-primary/5'}`}>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-sminy-primary" />
                      <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                        Sugestão de Investimento em Ads
                      </h4>
                      <InfoTooltip text="Investimento sugerido = Leads planejados × CPL histórico das suas campanhas. Sem histórico de CPL, a sugestão fica zerada." />
                    </div>
                    {loadingAdCpl && <Loader2 className="h-4 w-4 animate-spin text-sminy-primary" />}
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className={`rounded-xl border p-3 md:col-span-2 ${
                      isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Investimento sugerido</span>
                      <div className="mt-1 text-xl font-semibold text-sminy-primary">{formatCurrency(suggestedAdsInvestment)}</div>
                    </div>
                    <div className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>CPL histórico</span>
                      <div className="mt-1 text-lg font-semibold">{formatCurrency(adCpl?.cpl || 0)}</div>
                    </div>
                    <div className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Leads planejados</span>
                      <div className="mt-1 text-lg font-semibold">{formatNumber(plan.leads)}</div>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-950' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Invest. histórico</span>
                      <div className="mt-1 text-sm font-semibold">{formatCurrency(adCpl?.totalInvestment || 0)}</div>
                    </div>
                    <div className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-950' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Leads de ads</span>
                      <div className="mt-1 text-sm font-semibold">{formatNumber(adCpl?.totalLeads || 0)}</div>
                    </div>
                    <div className={`rounded-xl border p-3 ${
                      isDark ? 'border-gray-700 bg-gray-950' : 'border-gray-200 bg-white'
                    }`}>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Anúncios</span>
                      <div className="mt-1 text-sm font-semibold">{formatNumber(adCpl?.sourceCount || 0)}</div>
                    </div>
                  </div>

                  {!loadingAdCpl && !hasHistoricalCpl && (
                    <div className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                      isDark
                        ? 'border-amber-900/50 bg-amber-950/20 text-amber-200'
                        : 'border-amber-200 bg-amber-50 text-amber-700'
                    }`}>
                      Sem CPL histórico suficiente para estimar investimento em anúncios.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* FOOTER · SALVAR */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sminy-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar meta
              </button>
            </div>
            </div>
          )}
        </div>

        <ConfirmDeleteModal
          isOpen={Boolean(goalToArchive)}
          onClose={() => setGoalToArchive(null)}
          onConfirm={handleArchiveGoal}
          title="Arquivar meta"
          message={`Arquivar a meta "${goalToArchive?.name || ''}"? Ela sairá da lista de metas ativas, mas os dados históricos não serão apagados.`}
          confirmText="Sim, arquivar"
          cancelText="Cancelar"
        />

        <ConfirmDeleteModal
          isOpen={Boolean(archivedGoalToDelete)}
          onClose={() => setArchivedGoalToDelete(null)}
          onConfirm={handleDeleteArchivedGoal}
          title="Excluir meta arquivada"
          message={`Excluir definitivamente a meta "${archivedGoalToDelete?.name || ''}"? Essa ação remove apenas o registro da meta e não pode ser desfeita.`}
          confirmText="Excluir definitivamente"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

export default Metas;
